import { useEffect, useMemo, useState } from "react"
import { Entity } from "../../backend/types/entity.ts"
import { useGetMarketPricesQuery } from "../../api/price/price.ts"
import { Point } from "./diagram/point.ts"
import { Node } from "./diagram/diagram.ts"
import { useCurrentStep } from "../../hooks/useCurrentStep.ts"
import { useParams } from "react-router-dom"
import { useGetMoneyFlow } from "../../api/case/document.ts"

type TooltipProps = {
    name: string,
    id: string,
    type: string,
    amountUSDIn: number,
    amountUSDOut: number,
    addressesCount: number
}

export const useNodeTooltip = (
    entity: Entity | undefined,
    node: Node | undefined,
    position?: Point & { right: boolean }
) => {
    const { documentId } = useParams()
    const { data: moneyFlow } = useGetMoneyFlow(documentId)
    const { incomingValue, incomingValueNative, outgoingValue, outgoingValueNative } = node || {}
    const [show, setShow] = useState<boolean>(false)
    const { currentStep } = useCurrentStep()
    const [currencies, setCurrencies] = useState<string[]>([])
    const { data: prices, isPending } = useGetMarketPricesQuery(moneyFlow?.blockchain, entity?.ID, currentStep, currencies)
    const [tooltip, setTooltip] = useState<TooltipProps>({
        name: 'defname',
        type: 'defcat',
        id: 'id',
        amountUSDIn: 0,
        amountUSDOut: 0,
        addressesCount: 0
    })

    useEffect(() => {
        if (!entity) {
            return
        }

        const mergedTokens = new Set(
            [
                ...(incomingValueNative ?? []).map((asset) => asset.Address),
                ...(outgoingValueNative ?? []).map((asset) => asset.Address),
            ]
        )
        setCurrencies([...mergedTokens])

        setTooltip(() => ({
            id: entity.ID,
            name: entity.Name,
            amountUSDIn: incomingValue ?? 0,
            amountUSDOut: outgoingValue ?? 0,
            addressesCount: entity.AddressesLabeled.filter((addr) => !addr.Excluded).length,
            type: entity.Type
        }))
    }, [entity, incomingValue, incomingValueNative, outgoingValue, outgoingValueNative, prices])

    const tooltipPosition = useMemo(() => {
        let popupWidth = 450
        if (!incomingValue || !outgoingValue) {
            popupWidth = 300
        }
        return ({
            x: (position?.x ?? 0) + (position?.right ? -popupWidth : 20),
            y: (position?.y ?? 0) - 60
        })
    }, [incomingValue, outgoingValue, position?.right, position?.x, position?.y])


    return {
        show,
        tooltip,
        setShow,
        isPending,
        tooltipPosition,
        currencies
    }
}
